import { createClient } from '@supabase/supabase-js';
import { includesPrintGroup } from './printGroups';

const DEFAULT_WORKFORCE_PROJECT_REF = 'bhhhfdlwqhedxefudcdd';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const workforceProjectRef =
  (process.env.REACT_APP_WORKFORCE_SUPABASE_PROJECT_REF || DEFAULT_WORKFORCE_PROJECT_REF).trim()
  || DEFAULT_WORKFORCE_PROJECT_REF;
const workforceSupabaseUrl =
  (process.env.REACT_APP_WORKFORCE_SUPABASE_URL || '').trim()
  || `https://${workforceProjectRef}.supabase.co`;
const workforceSupabaseKey = (process.env.REACT_APP_WORKFORCE_SUPABASE_PUBLISHABLE_KEY || '').trim();

export const supabase = createClient(supabaseUrl, supabaseKey);
export const workforceSupabase = workforceSupabaseKey
  ? createClient(workforceSupabaseUrl, workforceSupabaseKey)
  : null;

export function isWorkforceSupabaseConfigured() {
  return !!workforceSupabase;
}

export function getSupabaseClient(target = 'primary') {
  return target === 'workforce' ? workforceSupabase : supabase;
}

export { workforceProjectRef };

/**
 * Subscribe to order_items changes for a specific monitor profile.
 * Used by the Kanban monitor app.
 *
 * @param {string[]} printGroups - e.g. ['küche'] or ['bar']
 * @param {function} onInsert - called with new item
 * @param {function} onUpdate - called with updated item
 * @param {function} onDelete - called with deleted item
 * @returns Supabase channel (call .unsubscribe() on cleanup)
 */
export function subscribeToMonitor(companyId, printGroups, { onInsert, onUpdate, onDelete }) {
  // Use company_id as a Realtime filter so this device only receives its tenant's events.
  const filter = companyId ? `company_id=eq.${companyId}` : undefined;

  const channel = supabase
    .channel(`monitor-${companyId || 'all'}-order-items`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'order_items', ...(filter && { filter }) },
      (payload) => {
        if (!printGroups.length || includesPrintGroup(printGroups, payload.new.print_group)) {
          onInsert?.(payload.new);
        }
      }
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'order_items', ...(filter && { filter }) },
      (payload) => {
        if (!printGroups.length || includesPrintGroup(printGroups, payload.new.print_group)) {
          onUpdate?.(payload.new);
        }
      }
    )
    .on(
      'postgres_changes',
      { event: 'DELETE', schema: 'public', table: 'order_items', ...(filter && { filter }) },
      (payload) => {
        onDelete?.(payload.old);
      }
    )
    .subscribe();

  return channel;
}

/**
 * Subscribe to table status changes.
 * Used by POS terminal to see real-time table occupancy.
 */
export function subscribeToTables(onChange) {
  return supabase
    .channel('pos-tables')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'pos_tables' },
      (payload) => onChange?.(payload)
    )
    .subscribe();
}
